import { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Button } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import chatIcon from '../../images/ChatButton.png';

const ChatbotSelector = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [learnwiseOpen, setLearnwiseOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const isMobile = useMediaQuery('(max-width:768px)');
  const menuItems = ['Faculty P.A.S.S. (BETA)', 'Technical Support'];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('#chatbot-selector')) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleChatButtonClick = () => {
    if (learnwiseOpen && window.learnwiseChat) {
      window.learnwiseChat.close();
      setLearnwiseOpen(false);
    } else {
      setMenuOpen(!menuOpen);
      setFocusedIndex(-1);
    }
  };

  useEffect(() => {
    if (learnwiseOpen) {
      // Disable focus on background elements only
      const allElements = document.querySelectorAll('*');
      allElements.forEach(el => {
        if (!el.closest('#chatbot-selector') && 
            !el.closest('[class*="learnwise"]') && 
            !el.closest('[id*="learnwise"]') &&
            !el.closest('iframe[src*="learnwise"]') &&
            !el.closest('[data-learnwise]')) {
          el.setAttribute('tabindex', '-1');
          el.setAttribute('data-original-tabindex', el.getAttribute('tabindex') || '');
        }
      });
    } else {
      // Restore original tabindex values
      const allElements = document.querySelectorAll('[data-original-tabindex]');
      allElements.forEach(el => {
        const originalTabindex = el.getAttribute('data-original-tabindex');
        if (originalTabindex === '') {
          el.removeAttribute('tabindex');
        } else {
          el.setAttribute('tabindex', originalTabindex);
        }
        el.removeAttribute('data-original-tabindex');
      });
    }
  }, [learnwiseOpen]);

  const handleKeyDown = (e) => {
    if (!menuOpen) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => prev < menuItems.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => prev > 0 ? prev - 1 : menuItems.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex === 0) handleLearnwiseClick();
        if (focusedIndex === 1) handleTACClick();
        break;
      case 'Escape':
        setMenuOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  const handleLearnwiseClick = () => {
    if (window.learnwiseChat) {
      window.learnwiseChat.open();
      setLearnwiseOpen(true);
    }
    setMenuOpen(false);
  };

  const handleTACClick = () => {
    if (window.openTAC) {
      window.openTAC();
    }
    setMenuOpen(false);
  };

  return (
    <SelectorContainer 
      id="chatbot-selector" 
      $isOpen={learnwiseOpen} 
      $isMobile={isMobile}
      role="region"
      aria-label="Chat support options"
    >
      <ChatButton 
        onClick={handleChatButtonClick} 
        onKeyDown={handleKeyDown}
        aria-label={learnwiseOpen ? "Close chat" : "Open chat support menu"}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        tabIndex={0}
        role="button"
      >
        {learnwiseOpen ? (
          <>
            <CloseIcon sx={{ fontSize: '18px', color: 'white', marginTop: '-4px', marginLeft: '-2px', marginRight: '4px' }} />
            <span style={{ marginTop: '-2px', marginLeft: '3px' }}>Close</span>
          </>
        ) : (
          <img src={chatIcon} alt="Open chat support menu" style={{ width: '104px', height: '43px' }} />
        )}
      </ChatButton>
      {menuOpen && (
        <Menu 
          role="menu" 
          aria-label="Chat support options"
        >
          <MenuItem 
            onClick={handleLearnwiseClick} 
            $focused={focusedIndex === 0}
            role="menuitem"
            tabIndex={focusedIndex === 0 ? 0 : -1}
            aria-label="Open Faculty P.A.S.S. BETA chat"
          >
            Faculty P.A.S.S. (BETA)
          </MenuItem>
          <MenuItem 
            onClick={handleTACClick} 
            $focused={focusedIndex === 1}
            role="menuitem"
            tabIndex={focusedIndex === 1 ? 0 : -1}
            aria-label="Open Technical Support chat"
          >
            Technical Support
          </MenuItem>
        </Menu>
      )}
    </SelectorContainer>
  );
};

const SelectorContainer = styled('div')(({ $isOpen, $isMobile }) => ({
  position: 'fixed',
  bottom: $isOpen ? ($isMobile ? '5px' : '30px') : '20px',
  right: $isOpen ? ($isMobile ? '5px' : '10px') : '20px',
  zIndex: 10000,
}));

const ChatButton = styled('button')(() => ({
  background: 'transparent',
  color: 'white',
  border: 'none',
  padding: '8px',
  borderRadius: '30px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  fontFamily: 'Roboto, sans-serif',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '104px',
  height: '43px',
  '&:hover': {
    opacity: 0.9,
  },
  '&:focus': {
    outline: 'auto',
    outlineColor: '#000000',
  },
  // Close state styling
  '&[aria-label*="Close"]': {
    backgroundColor: '#DC3727',
    '&:hover': {
      backgroundColor: '#DC3727',
    },
  },
}));

const Menu = styled('div')({
  position: 'absolute',
  bottom: '60px',
  right: 0,
  background: 'white',
  border: '1px solid #ddd',
  borderRadius: '8px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  minWidth: '200px',
});

const MenuItem = styled('div')(({ $focused }) => ({
  padding: '12px 16px',
  cursor: 'pointer',
  borderBottom: '1px solid #eee',
  fontFamily: 'Roboto, sans-serif',
  background: $focused ? '#f5f5f5' : 'transparent',
  '&:hover': {
    background: '#f5f5f5',
  },
  '&:last-child': {
    borderBottom: 'none',
  },
}));

export default ChatbotSelector;